import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader, Panel, Toolbar } from '../components/Page';
import { EmptyState, ErrorState, InlineError, TableSkeleton } from '../components/Feedback';
import { ApprovalReasonChip, ApprovalStatusChip, ConfidenceBadge } from '../components/Chips';
import { SelectField, TextAreaField, TextField, enumOptions, parseEnumValue } from '../components/Form';
import { SelectableRow, TableWrap } from '../components/Table';
import { SortableTh } from '../components/SortableTh';
import { Pagination } from '../components/Pagination';
import { Drawer } from '../components/Overlay';
import { DocumentPreview } from '../components/DocumentPreview';
import { useAuth } from '../lib/auth';
import { approvals } from '../lib/endpoints';
import { useAsync } from '../lib/hooks';
import { useSortableTable } from '../lib/useSortableTable';
import { useToast } from '../lib/toast';
import { errorMessage } from '../lib/api';
import {
  ApprovalStatus,
  ApprovalStatusLabels,
  STALE_APPROVAL_DETAIL,
  STALE_APPROVAL_TITLE,
} from '../lib/enums';
import { formatDateTime, formatMoney, formatRelative } from '../lib/format';
import type { ApprovalDto } from '../lib/types';

type DecisionKind = 'approve' | 'reject' | 'info';

type ApprovalColumn = 'document' | 'reason' | 'amount' | 'confidence' | 'requested' | 'status';

function approvalSortValue(approval: ApprovalDto, key: ApprovalColumn): string | number {
  switch (key) {
    case 'document':
      return approval.vendorName ?? approval.fileName;
    case 'reason':
      return approval.reason;
    case 'amount':
      return approval.amount ?? 0;
    case 'confidence':
      return approval.confidence ?? 0;
    case 'requested':
      return approval.createdUtc;
    case 'status':
      return approval.status;
    default:
      return '';
  }
}

export function ApprovalsPage(): JSX.Element {
  const { can, user } = useAuth();
  const toast = useToast();
  const canDecide = can('approvals.decide');

  const [statusFilter, setStatusFilter] = useState<ApprovalStatus | null>(ApprovalStatus.Pending);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selected, setSelected] = useState<ApprovalDto | null>(null);
  const [comment, setComment] = useState('');
  const [assignTo, setAssignTo] = useState('');
  const [pending, setPending] = useState<DecisionKind | null>(null);
  const [decisionError, setDecisionError] = useState<string | null>(null);

  const list = useAsync(
    (signal) => approvals.list({ status: statusFilter, page, pageSize }, signal),
    [statusFilter, page, pageSize],
  );

  const table = useSortableTable<ApprovalColumn>({
    storageKey: 'approvalsColumnWidths',
    columns: {
      document: { minWidth: 160 },
      reason: { minWidth: 140 },
      amount: { minWidth: 100 },
      confidence: { minWidth: 100 },
      requested: { minWidth: 130 },
      status: { minWidth: 120 },
    },
  });

  const openDrawer = (approval: ApprovalDto) => {
    setSelected(approval);
    setComment('');
    setAssignTo('');
    setDecisionError(null);
  };

  const decide = async (kind: DecisionKind) => {
    if (!selected) return;
    if (kind === 'info' && comment.trim().length === 0) {
      setDecisionError('A comment is required when requesting more information.');
      return;
    }
    setPending(kind);
    setDecisionError(null);
    try {
      if (kind === 'approve') {
        await approvals.approve(selected.id, { comment: comment.trim() || null });
        toast.success('Approved', `${selected.fileName} was approved for posting.`);
      } else if (kind === 'reject') {
        await approvals.reject(selected.id, { comment: comment.trim() || null });
        toast.success('Rejected', `${selected.fileName} was rejected.`);
      } else {
        await approvals.requestInfo(selected.id, {
          comment: comment.trim(),
          assignToEmail: assignTo.trim() || null,
        });
        toast.success('Information requested', 'The requester has been notified.');
      }
      setSelected(null);
      list.reload();
    } catch (error) {
      const message = errorMessage(error);
      setDecisionError(message);
      toast.error('Decision failed', message);
    } finally {
      setPending(null);
    }
  };

  // A stale approval is refused by the gateway whoever is asking, so it is called out separately
  // from an authority block: the reason a person cannot act on it is the document, not their role.
  const stale = selected?.isStale ?? false;
  const blocked = selected !== null && !selected.currentUserCanDecide;
  const decideDisabled = !canDecide || blocked || stale || pending !== null;

  return (
    <>
      <PageHeader
        title="Approvals"
        description={
          user?.approvalLimit !== null && user?.approvalLimit !== undefined
            ? `Your approval limit is ${formatMoney(user.approvalLimit)}.`
            : 'Documents routed here by a rule or by an operator.'
        }
        actions={
          <button type="button" className="btn btn-secondary" onClick={list.reload} disabled={list.loading}>
            {list.loading ? 'Refreshing…' : 'Refresh'}
          </button>
        }
      />

      <Panel>
        <Toolbar>
          <SelectField
            label="Status"
            value={statusFilter === null ? '' : String(statusFilter)}
            onChange={(value) => {
              setStatusFilter(parseEnumValue<ApprovalStatus>(value));
              setPage(1);
            }}
            options={enumOptions(ApprovalStatusLabels)}
            placeholder="All statuses"
          />
        </Toolbar>

        {list.loading && !list.data ? (
          <TableSkeleton rows={8} columns={6} />
        ) : list.error ? (
          <ErrorState message="The approval queue could not be loaded." detail={list.error} onRetry={list.reload} />
        ) : !list.data || list.data.items.length === 0 ? (
          <EmptyState
            title="Queue is clear"
            description="Nothing is waiting on an approval decision right now."
            icon="✓"
          />
        ) : (
          <>
            <TableWrap busy={list.loading} tableClassName="data-table-sortable">
              <colgroup>
                <col style={table.columnStyle('document')} />
                <col style={table.columnStyle('reason')} />
                <col style={table.columnStyle('amount')} />
                <col style={table.columnStyle('confidence')} />
                <col style={table.columnStyle('requested')} />
                <col style={table.columnStyle('status')} />
              </colgroup>
              <thead>
                <tr>
                  <SortableTh
                    label="Document"
                    sortKey="document"
                    currentSortKey={table.sortKey}
                    sortDir={table.sortDir}
                    onSort={table.toggleSort}
                    style={table.columnStyle('document')}
                    onResizeStart={table.beginResize('document')}
                  />
                  <SortableTh
                    label="Reason"
                    sortKey="reason"
                    currentSortKey={table.sortKey}
                    sortDir={table.sortDir}
                    onSort={table.toggleSort}
                    style={table.columnStyle('reason')}
                    onResizeStart={table.beginResize('reason')}
                  />
                  <SortableTh
                    label="Amount"
                    sortKey="amount"
                    currentSortKey={table.sortKey}
                    sortDir={table.sortDir}
                    onSort={table.toggleSort}
                    className="num"
                    style={table.columnStyle('amount')}
                    onResizeStart={table.beginResize('amount')}
                  />
                  <SortableTh
                    label="Confidence"
                    sortKey="confidence"
                    currentSortKey={table.sortKey}
                    sortDir={table.sortDir}
                    onSort={table.toggleSort}
                    style={table.columnStyle('confidence')}
                    onResizeStart={table.beginResize('confidence')}
                  />
                  <SortableTh
                    label="Requested"
                    sortKey="requested"
                    currentSortKey={table.sortKey}
                    sortDir={table.sortDir}
                    onSort={table.toggleSort}
                    style={table.columnStyle('requested')}
                    onResizeStart={table.beginResize('requested')}
                  />
                  <SortableTh
                    label="Status"
                    sortKey="status"
                    currentSortKey={table.sortKey}
                    sortDir={table.sortDir}
                    onSort={table.toggleSort}
                    style={table.columnStyle('status')}
                    onResizeStart={table.beginResize('status')}
                  />
                </tr>
              </thead>
              <tbody>
                {table.sort(list.data.items, approvalSortValue).map((approval) => (
                  <SelectableRow
                    key={approval.id}
                    selected={selected?.id === approval.id}
                    onSelect={() => openDrawer(approval)}
                    label={`${approval.vendorName ?? 'Unknown vendor'} ${approval.fileName}`}
                  >
                    <td>
                      <span className="cell-strong">{approval.vendorName ?? 'Unknown vendor'}</span>
                      <span className="cell-sub">
                        {approval.invoiceNumber ? `${approval.invoiceNumber} · ` : ''}
                        {approval.fileName}
                      </span>
                    </td>
                    <td>
                      <ApprovalReasonChip reason={approval.reason} detail={approval.reasonDetail} />
                      {approval.reasonDetail ? <span className="cell-sub">{approval.reasonDetail}</span> : null}
                    </td>
                    <td className="num">{formatMoney(approval.amount, approval.currencyCode)}</td>
                    <td>
                      <ConfidenceBadge value={approval.confidence} size="sm" />
                    </td>
                    <td>
                      {formatRelative(approval.createdUtc)}
                      <span className="cell-sub">{approval.requestedByEmail ?? '—'}</span>
                    </td>
                    <td>
                      <ApprovalStatusChip status={approval.status} />
                      {approval.isStale ? (
                        <span className="cell-flag warn" title={STALE_APPROVAL_DETAIL}>
                          no longer valid
                        </span>
                      ) : !approval.currentUserCanDecide &&
                        approval.status === ApprovalStatus.Pending ? (
                        <span className="cell-flag warn">blocked</span>
                      ) : null}
                    </td>
                  </SelectableRow>
                ))}
              </tbody>
            </TableWrap>

            <Pagination
              page={list.data.page}
              pageSize={list.data.pageSize}
              totalCount={list.data.totalCount}
              totalPages={list.data.totalPages}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          </>
        )}
      </Panel>

      <Drawer
        open={selected !== null}
        onClose={() => setSelected(null)}
        title={selected?.vendorName ?? selected?.fileName ?? 'Approval'}
        description={
          selected
            ? `${selected.invoiceNumber ? `Invoice ${selected.invoiceNumber} · ` : ''}${formatMoney(selected.amount, selected.currencyCode)}`
            : undefined
        }
        footer={
          selected ? (
            <>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => decide('info')}
                disabled={decideDisabled}
              >
                {pending === 'info' ? 'Sending…' : 'Request more info'}
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => decide('reject')}
                disabled={decideDisabled}
              >
                {pending === 'reject' ? 'Rejecting…' : 'Reject'}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => decide('approve')}
                disabled={decideDisabled}
              >
                {pending === 'approve' ? 'Approving…' : 'Approve'}
              </button>
            </>
          ) : undefined
        }
      >
        {selected ? (
          <div className="approval-drawer">
            <div className="approval-summary">
              <ApprovalStatusChip status={selected.status} />
              <ApprovalReasonChip reason={selected.reason} detail={selected.reasonDetail} />
              <ConfidenceBadge value={selected.confidence} label="Extraction confidence" />
            </div>

            {stale ? (
              <div className="banner banner-warn banner-inline" role="alert">
                <div className="banner-body">
                  <p className="banner-title">{STALE_APPROVAL_TITLE}</p>
                  <p className="banner-detail">{STALE_APPROVAL_DETAIL}</p>
                  <p className="banner-detail">
                    It was raised against revision {selected.documentRevision}; the document has
                    moved on since. Approving an old revision would clear an amount that is no longer
                    the one that would post. Open the document below and send it for approval again.
                  </p>
                </div>
              </div>
            ) : blocked ? (
              <div className="banner banner-warn banner-inline" role="alert">
                <div className="banner-body">
                  <p className="banner-title">You cannot decide this approval</p>
                  <p className="banner-detail">
                    {selected.blockedReason ??
                      'Another approver must handle this item — it is outside your authority.'}
                  </p>
                </div>
              </div>
            ) : null}

            {!canDecide ? (
              <div className="banner banner-neutral banner-inline">
                <div className="banner-body">
                  <p className="banner-title">Read-only</p>
                  <p className="banner-detail">Your role can view approvals but not decide them.</p>
                </div>
              </div>
            ) : null}

            <dl className="kv-list kv-cols-2">
              <div className="kv">
                <dt className="kv-label">Amount</dt>
                <dd className="kv-value">{formatMoney(selected.amount, selected.currencyCode)}</dd>
              </div>
              <div className="kv">
                <dt className="kv-label">Requested by</dt>
                <dd className="kv-value">{selected.requestedByEmail ?? '—'}</dd>
              </div>
              <div className="kv">
                <dt className="kv-label">Assigned to</dt>
                <dd className="kv-value">{selected.assignedToEmail ?? 'Anyone with authority'}</dd>
              </div>
              <div className="kv">
                <dt className="kv-label">Raised</dt>
                <dd className="kv-value">{formatDateTime(selected.createdUtc)}</dd>
              </div>
              {selected.decidedByEmail ? (
                <div className="kv kv-wide">
                  <dt className="kv-label">Decision</dt>
                  <dd className="kv-value">
                    {selected.decidedByEmail} · {formatDateTime(selected.decidedUtc)}
                    {selected.decisionComment ? ` — ${selected.decisionComment}` : ''}
                  </dd>
                </div>
              ) : null}
            </dl>

            <div className="approval-preview">
              <DocumentPreview documentId={selected.documentId} fileName={selected.fileName} />
            </div>

            <Link className="btn btn-ghost btn-sm" to={`/purchase-invoices?document=${selected.documentId}`}>
              Open full review screen
            </Link>

            {canDecide ? (
              <>
                <TextAreaField
                  label="Comment"
                  value={comment}
                  onChange={setComment}
                  placeholder="Recorded on the audit trail. Required when requesting more information."
                />
                <TextField
                  label="Reassign to (email)"
                  type="email"
                  value={assignTo}
                  onChange={setAssignTo}
                  placeholder="Optional — used when requesting more information"
                />
                <InlineError message={decisionError} />
              </>
            ) : null}
          </div>
        ) : null}
      </Drawer>
    </>
  );
}
